import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import CustomToast from 'components/Toast';
import { useAppVersion } from 'utils/hooks/useAppVersion';
import { ROUTES } from 'dictionary/routes';
import styles from './index.module.scss';

/**
 * Renders nothing - it only watches for a newer build and reacts to it.
 * Mounted once, near the root (see src/context/AppContext/index.js), so it
 * sees every navigation in the app.
 *
 * Two behaviors, deliberately both:
 *   1. A toast with a manual "Refresh" link, shown once per staleness.
 *   2. A silent auto-reload on the *next* navigation after staleness is
 *      detected - except while the shopper is mid-return
 *      (ROUTES.REQUEST.ROOT), which must never be interrupted.
 */
export default function VersionWatcher() {
  const isStale = useAppVersion();
  const location = useLocation();
  const notifiedRef = useRef(false);
  const staleAtLocationRef = useRef(null);

  // Capture the location present at the moment staleness is first detected,
  // not on every subsequent navigation - deliberately omits `location` from
  // the deps so this only runs when `isStale` flips.
  useEffect(() => {
    if (isStale && staleAtLocationRef.current === null) {
      staleAtLocationRef.current = location;

      if (!notifiedRef.current) {
        notifiedRef.current = true;
        CustomToast({
          msg: (
            <span>
              A new version of this page is available.
              <button
                type="button"
                className={styles.refreshLink}
                onClick={() => window.location.reload()}
              >
                Refresh
              </button>
            </span>
          ),
          type: 'info',
          autoClose: false,
        });
      }
    }
  }, [isStale]);

  useEffect(() => {
    if (!isStale || staleAtLocationRef.current === null) return;
    if (location === staleAtLocationRef.current) return; // no navigation yet
    if (location.pathname.startsWith(ROUTES.REQUEST.ROOT)) return; // never interrupt an in-progress return
    window.location.reload();
  }, [isStale, location]);

  return null;
}
